import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { continueApplicationProcessing } from './application';
import { Address, INITIAL_ADDRESS } from '../../../domain/address';
import { FleetBase, PartialFleetBase } from '../../../domain/fleet';
import { Fleet } from '../../../domain/qualification';
import { cleanInputObject } from '../../../general/utils/form';
import { RootState } from '../../../store';
import { DataLoadState, initialDataLoadState } from '../../../store/reducers/common';

type BusinessInfoState = {
  info: FleetBase;
} & DataLoadState;

export type BusinessPageOnePayload = Pick<
  FleetBase,
  'ein' | 'name' | 'yearsOfOperation' | 'businessType' | 'industryId'
>;
export const businessPageOneKeys: Array<keyof BusinessPageOnePayload> = [
  'ein',
  'name',
  'yearsOfOperation',
  'businessType',
  'industryId',
];

export type BusinessPageTwoPayload = Pick<FleetBase, 'annualRevenue' | 'fleetSize' | 'monthlyFuelSpend' | 'address'>;
export const businessPageTwoKeys: Array<keyof BusinessPageTwoPayload> = [
  'annualRevenue',
  'fleetSize',
  'monthlyFuelSpend',
  'address',
];

export type BusinessPageVerifyPayload = Pick<FleetBase, 'ein' | 'name' | 'businessType' | 'address'>;
export const businessPageVerifyKeys: Array<keyof BusinessPageVerifyPayload> = [
  'ein',
  'name',
  'businessType',
  'address',
];

export type SpendIntentPayload = Pick<
  FleetBase,
  | 'monthlyNonFuelSpend'
  | 'fleetAndMaintenance'
  | 'travelAndRestaurants'
  | 'materialsAndSupplies'
  | 'generalBusiness'
  | 'defAndVehicleSupplies'
  | 'iceWaterFood'
>;

export const spendIntentKeys: Array<keyof SpendIntentPayload> = [
  'monthlyNonFuelSpend',
  'fleetAndMaintenance',
  'travelAndRestaurants',
  'materialsAndSupplies',
  'generalBusiness',
  'defAndVehicleSupplies',
  'iceWaterFood',
];

export const spendIntentKeysV2: Array<keyof SpendIntentPayload> = [
  'monthlyNonFuelSpend',
  'fleetAndMaintenance',
  'travelAndRestaurants',
  'materialsAndSupplies',
];

export const monthlyNonFuelSpendIntentKeys: Array<keyof SpendIntentPayload> = [
  'monthlyNonFuelSpend',
  'fleetAndMaintenance',
  'travelAndRestaurants',
  'materialsAndSupplies',
];

export type PreviewCardPayload = {
  dbaName: string;
  address: Address;
  numCardsToOrder: number;
  cardRestriction: string | null;
};

const INITIAL_FLEET_INFO: FleetBase = {
  name: '',
  dbaName: null,
  ein: '',
  address: INITIAL_ADDRESS,
  shippingAddress: null,
  numCardsToOrder: null,
  yearsOfOperation: '',
  businessType: '',
  industryId: '',
  annualRevenue: null,
  monthlyFuelSpend: null,
  fleetSize: null,
  monthlyNonFuelSpend: null,
  fleetAndMaintenance: null,
  travelAndRestaurants: null,
  materialsAndSupplies: null,
  generalBusiness: null,
  defAndVehicleSupplies: null,
  iceWaterFood: null,
  cardRestriction: null,
};

const initialState: BusinessInfoState = {
  ...initialDataLoadState,
  info: INITIAL_FLEET_INFO,
};

const slice = createSlice({
  name: 'businessInfo',
  initialState,
  reducers: {
    saveBusinessInfoOne: (state, action: PayloadAction<BusinessPageOnePayload>) => ({
      ...state,
      info: {
        ...state.info,
        ...action.payload,
      },
    }),
    saveBusinessInfoTwo: (state, action: PayloadAction<BusinessPageTwoPayload>) => ({
      ...state,
      info: {
        ...state.info,
        ...action.payload,
      },
    }),
    saveBusinessInfoVerify: (state, action: PayloadAction<BusinessPageVerifyPayload>) => ({
      ...state,
      info: {
        ...state.info,
        ...action.payload,
      },
    }),
    saveSpendIntent: (state, action: PayloadAction<SpendIntentPayload>) => ({
      ...state,
      info: {
        ...state.info,
        ...action.payload,
      },
    }),
    updateBusinessAddress: (state, action: PayloadAction<Address>) => ({
      ...state,
      info: {
        ...state.info,
        address: action.payload,
      },
    }),
    savePreviewCardInfo: (state, action: PayloadAction<PreviewCardPayload>) => ({
      ...state,
      info: {
        ...state.info,
        dbaName: action.payload.dbaName,
        shippingAddress: action.payload.address,
        numCardsToOrder: action.payload.numCardsToOrder,
        cardRestriction: action.payload.cardRestriction,
      },
    }),
    updateShippingAddress: (state, action: PayloadAction<Address>) => ({
      ...state,
      info: {
        ...state.info,
        shippingAddress: action.payload,
      },
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(
      continueApplicationProcessing,
      (
        state,
        {
          payload: {
            response: { application },
          },
        },
      ) => {
        if (application?.fleet) {
          const fleet = parseFleet(application.fleet);
          return {
            ...state,
            info: {
              ...INITIAL_FLEET_INFO,
              ...fleet,
              address: {
                ...INITIAL_ADDRESS,
                ...fleet.address,
              },
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              shippingAddress: fleet.shippingAddress
                ? {
                    ...INITIAL_ADDRESS,
                    ...fleet.shippingAddress,
                  }
                : null,
            },
          };
        } else {
          return state;
        }
      },
    );
  },
});

function parseFleet(fleet: Fleet): PartialFleetBase {
  const {
    streetAddress,
    streetAddress2,
    city,
    state,
    zip,
    shippingStreetAddress,
    shippingStreetAddress2,
    shippingCity,
    shippingState,
    shippingZip,
    ...info
  } = fleet;
  return cleanInputObject({
    raw: {
      ...info,
      address: {
        streetAddress,
        streetAddress2,
        city,
        state,
        zip,
      },
      shippingAddress:
        shippingStreetAddress || shippingStreetAddress2 || shippingCity || shippingState || shippingZip
          ? {
              streetAddress: shippingStreetAddress,
              streetAddress2: shippingStreetAddress2,
              city: shippingCity,
              state: shippingState,
              zip: shippingZip,
            }
          : null,
    },
  });
}

export const businessInfoReducer = slice.reducer;
export const {
  saveBusinessInfoOne,
  saveBusinessInfoTwo,
  saveBusinessInfoVerify,
  saveSpendIntent,
  updateBusinessAddress,
  savePreviewCardInfo,
  updateShippingAddress,
} = slice.actions;

export const selectBusinessInfo = (state: RootState) => ({
  businessInfo: state.businessInfo.info,
  loading: state.businessInfo.loading,
  error: state.businessInfo.error,
});
